import { CopyIcon, EllipsisVerticalIcon, StopCircleIcon, Trash2Icon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import { MODAL_CONFIRMATION, MODAL_DELETE } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { RunStatus, SimulationRun } from '@/modules/simulations/types';
import { popModal, pushModal } from '@/redux/modals/actions';

// Exhaustive Record so any new RunStatus value forces a decision here at compile time.
const IS_ACTIVE_RUN_STATUS: Record<RunStatus, boolean> = {
  pending: true,
  running: true,
  completed: false,
  failed: false,
  canceled: false,
};

interface ActionsCellProps {
  run: SimulationRun;
  onStop: (runId: string) => void;
  onDelete: (runId: string) => void;
}

export const ActionsCell = ({ run, onStop, onDelete }: ActionsCellProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isActive = IS_ACTIVE_RUN_STATUS[run.status];

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(run.run_id);
      toast.success(t('common.copied'));
    } catch {
      toast.error(t('common.error_copying_id'));
    }
  };

  const handleStop = () => {
    dispatch(
      pushModal(MODAL_CONFIRMATION, {
        title: t('simulations.stop_confirm_title'),
        subtitle: <Trans i18nKey="simulations.stop_confirm_description" />,
        primaryButtonText: t('simulations.stop'),
        onConfirm: () => {
          onStop(run.run_id);
          dispatch(popModal());
        },
      })
    );
  };

  const handleDelete = () => {
    dispatch(
      pushModal(MODAL_DELETE, {
        title: t('simulations.delete_run_confirm_title'),
        subtitle: <Trans i18nKey="simulations.delete_run_confirm_description" />,
        onDelete: () => {
          onDelete(run.run_id);
          dispatch(popModal());
        },
      })
    );
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          variant="ghost-neutral"
          size="icon-sm"
        >
          <EllipsisVerticalIcon size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onSelect={handleCopyId}>
          <CopyIcon size={16} className="text-muted-foreground" />
          {t('simulations.copy_id')}
        </DropdownMenuItem>

        {isActive && (
          <DropdownMenuItem onSelect={handleStop} variant="destructive">
            <StopCircleIcon size={16} />
            {`${t('simulations.stop')}...`}
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={handleDelete} variant="destructive">
          <Trash2Icon size={16} />
          {`${t('common.delete')}...`}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
